import _ from 'underscore';
import React, { useCallback, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { TextField, ChoiceGroup, Callout, DirectionalHint } from '@bingads-webui-react/fabric-control';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import {
  civilDateRangeEqual,
  CSMLTimeFrame
} from '@bingads-webui-campaign-react/asset-library-model';
import { CustomDropdown } from '../custom-dropdown-component';
import { SimpleDateRangePicker } from '../simple-date-range-picker';
import { getDateAddedFilterStyle } from './style';

const iconProps = { iconName: 'Calendar' };

const DateAddedFilterInternal = ({
  defaultTimeFrame,
  defaultCustomDateRange,
  choiceOptions,
  timeZone,
  dateFormat,
  dropdownWidth,
  onChange,
  onDismiss,
  focused,
  i18n,
  classes,
}) => {
  //
  const [timeframe, setTimeFrame] = useState(defaultTimeFrame);
  // date range user selected for 'Custom'
  const [customDateRange, setCustomDateRange] = useState(defaultCustomDateRange);

  //
  // choice group handling
  const selectedChoice = useMemo(
    () => _.find(choiceOptions, r => r.key === timeframe),
    [timeframe, choiceOptions]
  );

  const onChoiceChange = useCallback((evt, option) => {
    if (option && option.key) {
      setTimeFrame(option.key);
    }
  }, []);

  //
  // dropdown related handling
  const formatDate = useCallback(
    date => i18n.formatCivilDate(date, { raw: dateFormat, timeZone }),
    [dateFormat, i18n, timeZone]
  );

  const customRangeStartStr = useMemo(
    () => formatDate(customDateRange.start),
    [formatDate, customDateRange.start]
  );

  const customRangeEndStr = useMemo(
    () => formatDate(customDateRange.end),
    [formatDate, customDateRange.end]
  );

  // the string shown on dropdown title
  const dropdownTitle = useMemo(() => {
    if (!selectedChoice) {
      return null;
    }
    if (selectedChoice.key === CSMLTimeFrame.Custom) {
      return `${customRangeStartStr} - ${customRangeEndStr}`;
    }
    return selectedChoice.text;
  }, [customRangeEndStr, customRangeStartStr, selectedChoice]);

  // when the X button on dropdown title is clicked
  const onDropdownClear = useCallback((isDropdownOpen) => {
    setTimeFrame(CSMLTimeFrame.Anytime);
    if (!isDropdownOpen && onChange && defaultTimeFrame !== CSMLTimeFrame.Anytime) {
      onChange({ timeframe: CSMLTimeFrame.Anytime, customDateRange });
    }
  }, [customDateRange, defaultTimeFrame, onChange]);

  // when dropdown list is dismissed
  const onDropdownDismiss = useCallback(() => {
    if (onChange) {
      if (defaultTimeFrame !== timeframe
        || !civilDateRangeEqual(defaultCustomDateRange, customDateRange)) {
        onChange({ timeframe, customDateRange }, dropdownTitle);
      }
    }
    if (onDismiss) {
      onDismiss({ timeframe, customDateRange }, dropdownTitle);
    }
  }, [customDateRange, defaultCustomDateRange, defaultTimeFrame, dropdownTitle, onChange, onDismiss, timeframe]);

  //
  // date range picker related handling
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  // selecting 'start' or 'end' on date range picker
  const isSelectingStartDate = useRef(true);

  const onSelectedDateRangeChange = useCallback((newRange) => {
    setCustomDateRange(newRange);
  }, []);

  const onDatepickerDismiss = useCallback(() => {
    setIsDatePickerOpen(false);
  }, []);

  const openDatePickerForStart = useCallback(() => {
    setIsDatePickerOpen(true);
    isSelectingStartDate.current = true;
  }, []);

  const openDatePickerForEnd = useCallback(() => {
    setIsDatePickerOpen(true);
    isSelectingStartDate.current = false;
  }, []);

  return (
    <CustomDropdown
      title={dropdownTitle}
      placeholder={i18n.getString(_TL_('Date added'))}
      dropdownWidth={dropdownWidth}
      onClear={onDropdownClear}
      onDismiss={onDropdownDismiss}
      focused={focused}
    >
      <div className={classes.dateAddedFilterDowndown}>
        <div className="dropdown-title">{i18n.getString(_TL_('Date added'))}</div>
        <ChoiceGroup
          className="dropdown-timeframe-choices"
          selectedKey={selectedChoice ? selectedChoice.key : ''}
          options={choiceOptions}
          onChange={onChoiceChange}
        />
        {timeframe === CSMLTimeFrame.Custom && (
          <div id="custom-date-picker" className="custom-date-picker-container">
            <TextField
              className="dropdown-value-input"
              label={i18n.getString(_TL_('Start date'))}
              value={customRangeStartStr}
              iconProps={iconProps}
              readOnly
              onFocus={openDatePickerForStart}
            />
            <TextField
              className="dropdown-value-input"
              label={i18n.getString(_TL_('End date'))}
              value={customRangeEndStr}
              iconProps={iconProps}
              readOnly
              onFocus={openDatePickerForEnd}
            />
            { isDatePickerOpen && (
              <Callout
                isBeakVisible={false}
                target="#custom-date-picker"
                directionalHint={DirectionalHint.rightCenter}
                directionalHintFixed={false}
                // dismissOnTargetClick
                gapSpace={20}
                onDismiss={onDatepickerDismiss}
              >
                <SimpleDateRangePicker
                  defaultSelectedDateRange={customDateRange}
                  defaultIsSelectingStartDate={isSelectingStartDate.current}
                  onDateRangeChange={onSelectedDateRangeChange}
                  i18n={i18n}
                  timeZone={timeZone}
                />
              </Callout>
            )}
          </div>
        )}
      </div>
    </CustomDropdown>
  );
};

DateAddedFilterInternal.propTypes = {
  defaultTimeFrame: PropTypes.string,
  defaultCustomDateRange: PropTypes.shape({}),
  choiceOptions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    text: PropTypes.string,
  })).isRequired,
  timeZone: PropTypes.string.isRequired,
  dateFormat: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onDismiss: PropTypes.func,
  focused: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
    formatCivilDate: PropTypes.func,
  }).isRequired,
  dropdownWidth: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  classes: PropTypes.shape({
    dateAddedFilterDowndown: PropTypes.string,
  }).isRequired,
};

DateAddedFilterInternal.defaultProps = {
  defaultTimeFrame: undefined,
  defaultCustomDateRange: {},
  onChange: undefined,
  onDismiss: undefined,
  focused: false,
  dropdownWidth: 0,
};

export const DateAddedFilter
  = withDefaultStyles(DateAddedFilterInternal, getDateAddedFilterStyle);
